<template>
    <div v-if="isMounted" class="permissions" v-can="'permission.access'">
        <ds-table
            :data="permissionsList"
            :heads="heads"
            :max-items="50"
            :view-items="[50, 100, 500]"
            expand
            search
        >
            <template v-slot:header>
                <vs-button v-can="'permission.create'" @click="create" class="mr-4 whitespace-no-wrap" color="primary" type="border" v-bind="$iconData('icon-plus', 'fi-rr-plus')">{{$t('modals.Create_module')}}</vs-button>
                <vs-button v-can:show="'permission.create'" disabled class="mr-4 whitespace-no-wrap" color="primary" type="border" v-bind="$iconData('icon-plus', 'fi-rr-plus')">{{$t('modals.Create_module')}}</vs-button>
            </template>

            <template v-slot:permissions="{permissions}">
                <vs-chip v-tooltip.position.left="permissions.permissionsList.join('<br>')">
                    <vs-avatar color="#f0f0f0" text-color="#175ea5" v-bind="$iconData('icon-book', 'fi-rr-book')"></vs-avatar>
                    <span>{{ permissions.permissionsLength }}</span>
                </vs-chip>
            </template>

            <template v-slot:description="{description}">
                <div class="product-name font-medium truncate">{{ description.description }}</div>
            </template>

            <template v-slot:role="{role}">
                <vs-chip v-tooltip.position.left="role.rolesList.join('<br>')">
                    <vs-avatar color="#f0f0f0" text-color="#175ea5" v-bind="$iconData('icon-git-merge', 'fi-rs-code-merge')"></vs-avatar>
                    <span>{{ role.rolesLength }}</span>
                </vs-chip>
            </template>

            <template v-slot:actions="{actions}">
                <div class="flex">
                    <vs-button v-can="'permission.create'" @click.stop="addPermission(actions)" v-tooltip="$t('modals.Add_permission')" radius color="primary" type="flat" v-bind="$iconData('icon-plus', 'fi-rr-plus')"></vs-button>
                    <vs-button v-can:show="'permission.create'" disabled radius color="primary" type="flat" v-bind="$iconData('icon-plus', 'fi-rr-plus')"></vs-button>
                    <vs-button v-can="'permission.update'" @click.stop="editPermission(actions)" v-tooltip="$t('modals.Edit_module')" radius color="primary" type="flat" v-bind="$iconData('icon-edit-2', 'fi-rr-file-edit')"></vs-button>
                    <vs-button v-can:show="'permission.update'" disabled radius color="primary" type="flat" v-bind="$iconData('icon-edit-2', 'fi-rr-file-edit')"></vs-button>
                </div>
            </template>

            <template v-slot:expand="{expand}">
                <PermissionsDetails :data="expand" :roles="rolesItems" />
            </template>
        </ds-table>
    </div>
</template>

<script>
import ErrorHandler from '@/utils/errorHandler'
import send from '@/utils/fetch'
import EventBus from '@/store/eventBus'
import PermissionsDetails from '@/views/settings/permissions/PermissionsDetails'

export default {
    data() {
        return {
            isMounted: false,
            permissions: [],
            rolesItems: [],
            heads: [
                {name: 'global.Name', key: 'name', sortKey: 'name', value: 'name', display: true},
                {name: 'global.Permissions', key: 'permissions', sortKey: 'permissionsLength', value: 'permissions', display: true},
                {name: 'global.Description', key: 'description', sortKey: 'description', value: 'description', display: true},
                {name: 'global.Role', key: 'role', sortKey: 'rolesLength', value: 'role', display: true},
                {name: 'global.Slug', key: 'slug', sortKey: 'slug', value: 'slug', display: true},
                {name: 'global.Actions', key: 'actions', value: 'actions', display: true},
            ]
        }
    },

    components: {
        PermissionsDetails
    },

    computed: {
        permissionsList() {
            return this.permissions
        }
    },

    methods: {
        create() {
            EventBus.$emit('emitPopupEvent', {type: 'createPermission', name: this.$t('modals.Create_module'), ids: {rolesItems: this.rolesItems}})
        },

        editPermission(data) {
            EventBus.$emit('emitPopupEvent', {type: 'editPermission', name: this.$t('modals.Edit_module'), ids: {data, rolesItems: this.rolesItems}})
        },

        addPermission(data) {
            EventBus.$emit('emitPopupEvent', {type: 'addPermissionModule', name: this.$t('modals.Add_permission'), ids: {data, rolesItems: this.rolesItems}})
        },

        async getRoles() {
            try {
                return await send(this.$url.role.get, 'GET')
                    .then(response => response.data.result)
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        renderRole(roles) {
            const list = roles.map(item => this.rolesItems.find(role => ~~role.value === ~~item))
            return list.map(item => {
                if(item) {
                    return item.label
                }
            })
        },

        mutations(array) {
            const findRole = id => {
                const role = this.rolesItems.find(role => role.value === id)
                return role ? role.label : id
            }

            return array.map(item => {
                item.permissions = item.permissions.map(item => {
                    item.rolesList = item.role_ids.map(id => findRole(id))
                    item.rolesLength = item.rolesList.length
                    return item
                })

                const permissions = item.permissions.map(item => item.name)
                const roles = item.role_ids.map(id => findRole(id))

                item.rolesList = roles
                item.rolesLength = roles.length
                item.permissionsList = permissions
                item.permissionsLength = permissions.length

                return item
            })
        },

        async getPermissions() {
            try {
                this.permissions = await send(this.$url.permission.get, 'GET')
                    .then(response => this.mutations(response.data.result).reverse())
            } catch (e) {
                new ErrorHandler(e).send
            }
        }
    },

    async mounted() {
        const roles = await this.getRoles()
        this.rolesItems = roles.map(item => ({label: item.name, value: item.id}))

        await this.getPermissions()

        EventBus.$on('SUCCESS', async () => {
            await this.getPermissions()
        })

        this.isMounted = true
    }
}
</script>

<style lang="scss">
.permissions {
    &__buttons {
        width: 100%;
        button {
            margin-bottom: 0 !important;
        }
    }
    .content-tr-expand {
        justify-content: left !important;
    }
    .vs-table {
        &-text {
            text-transform: uppercase;
        }
        &--tbody-table {
            tr {
                &.spacer {
                    background: none;
                    box-shadow: none;

                    td {
                        padding: 5px !important;
                    }
                }

                td {
                    &:first-child {
                        border-top-left-radius: .5rem;
                        border-bottom-left-radius: .5rem;

                        &.td-check {
                            max-width: 30px;
                            padding: 20px 0 20px 15px !important;
                        }
                    }

                    &:last-child {
                        border-top-right-radius: .5rem;
                        border-bottom-right-radius: .5rem;
                    }

                    .vs-button.small {
                        padding: 0.5rem 1rem;

                        &.includeIcon {
                            padding: 0.5rem 1rem 0.5rem 0.7rem;
                        }
                    }
                }
            }
        }
    }
}


.vs-table--thead tr {
    background: none;
}
</style>
